import React from 'react'
import { Card } from '../../game/types'
import { rarityStars } from '../../game/cards'
import { AUGMENT_SPRITE } from '../../game/augments'
import { SpriteImg } from '../ui/SpriteImg'
import { useCardDetail } from './useCardDetail'
import { getSynergyGroups } from '../../game/synergies'
import { SynergyBadges } from './SynergyBadges'
import { Icon } from '../ui/icons/Icon'

const UPGRADE_SPRITE: Record<string, string> = {
  buffAttack: 'upgrade-attack',
  healUnits:  'upgrade-heal',
  buffSpeed:  'upgrade-speed',
  buffMaxHp:  'upgrade-hp',
  buffRange:  'upgrade-range',
}

// ─── Card type category ───────────────────────────────────

type CardCategory = 'unit' | 'hero' | 'spawner' | 'defensive' | 'support' | 'buff'

function getCardCategory(card: Card): CardCategory {
  if (card.isHero) return 'hero'
  if (card.cardType === 'upgrade') return 'buff'
  if (card.cardType === 'unit') return 'unit'
  // structure subtypes
  const fx = card.unit?.structureEffect?.type
  if (fx === 'spawn') return 'spawner'
  if (card.unit?.isWall || fx === 'attackAura' || fx === 'slowZone') return 'defensive'
  return 'support' // mana, manaSpeed, healAura, repairAura, unknown
}

const CATEGORY_LABEL: Record<CardCategory, string> = {
  unit:      'UNIT',
  hero:      'HERO',
  spawner:   'SPAWN',
  defensive: 'DEF',
  support:   'SUP',
  buff:      'BUFF',
}

// Inline SVG icons (12×12 viewBox, pixel-art style)
function UnitIcon() {
  return (
    <svg viewBox="0 0 12 12" width="10" height="10" fill="none">
      {/* sword blade */}
      <line x1="6" y1="1" x2="6" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
      {/* crossguard */}
      <line x1="3" y1="4" x2="9" y2="4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
      {/* pommel */}
      <rect x="5" y="9" width="2" height="2" fill="currentColor"/>
    </svg>
  )
}

function HeroIcon() {
  return (
    <svg viewBox="0 0 12 12" width="10" height="10" fill="none">
      {/* crown base */}
      <rect x="2" y="7" width="8" height="2" fill="currentColor"/>
      {/* three crown points */}
      <rect x="2" y="3" width="2" height="4" fill="currentColor"/>
      <rect x="5" y="2" width="2" height="5" fill="currentColor"/>
      <rect x="8" y="3" width="2" height="4" fill="currentColor"/>
    </svg>
  )
}

function SpawnerIcon() {
  return (
    <svg viewBox="0 0 12 12" width="10" height="10" fill="none">
      {/* building base */}
      <rect x="1" y="5" width="7" height="6" stroke="currentColor" strokeWidth="1" fill="none"/>
      {/* roof peak */}
      <polyline points="1,5 4.5,2 8,5" stroke="currentColor" strokeWidth="1" fill="none"/>
      {/* exit arrow */}
      <line x1="9" y1="8" x2="12" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
      <polyline points="10,6 12,8 10,10" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="square" strokeLinejoin="miter"/>
    </svg>
  )
}

function DefensiveIcon() {
  return (
    <svg viewBox="0 0 12 12" width="10" height="10" fill="none">
      {/* shield outline */}
      <path d="M6 1 L10 3 L10 7 Q10 10 6 11 Q2 10 2 7 L2 3 Z" stroke="currentColor" strokeWidth="1" fill="none"/>
      {/* shield cross */}
      <line x1="6" y1="3.5" x2="6" y2="8.5" stroke="currentColor" strokeWidth="1" strokeLinecap="square"/>
      <line x1="3.5" y1="6" x2="8.5" y2="6" stroke="currentColor" strokeWidth="1" strokeLinecap="square"/>
    </svg>
  )
}

function SupportIcon() {
  return (
    <svg viewBox="0 0 12 12" width="10" height="10" fill="none">
      {/* diamond gem */}
      <polygon points="6,1 10,5 6,11 2,5" stroke="currentColor" strokeWidth="1" fill="none"/>
      {/* facet line */}
      <line x1="2" y1="5" x2="10" y2="5" stroke="currentColor" strokeWidth="0.8"/>
      <line x1="6" y1="1" x2="4" y2="5" stroke="currentColor" strokeWidth="0.8"/>
      <line x1="6" y1="1" x2="8" y2="5" stroke="currentColor" strokeWidth="0.8"/>
    </svg>
  )
}

function BuffIcon() {
  return (
    <svg viewBox="0 0 12 12" width="10" height="10" fill="none">
      {/* lightning bolt */}
      <polyline points="8,1 4,6 7,6 4,11" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="square" strokeLinejoin="miter"/>
    </svg>
  )
}

const CATEGORY_ICON: Record<CardCategory, React.ReactNode> = {
  unit:      <UnitIcon />,
  hero:      <HeroIcon />,
  spawner:   <SpawnerIcon />,
  defensive: <DefensiveIcon />,
  support:   <SupportIcon />,
  buff:      <BuffIcon />,
}

interface Props {
  card: Card
  canAfford?: boolean
  disabled?: boolean
  onClick?: () => void
  lockedSecs?: number   // hero cards: seconds remaining until playable (0 = unlocked)
  upgradeable?: boolean // collection: show UPGRADE badge
  displayCost?: number  // override displayed cost (e.g. after archetype discounts)
  showDetails?: boolean  // collection: show details button
  deckMatches?: number   // deck builder: how many deck cards this one pairs with
  selected?: boolean     // deck builder / picker screens: this tile is the active selection
  isNew?: boolean        // collection: unseen since last acquired
  /**
   * Battle hand: show only what a play decision needs — name, icon, cost, type.
   * Drops the stat line and rarity stars, and tightens the art/padding. The hand
   * is the single biggest consumer of the battlefield's vertical budget, and every
   * pixel it gives back widens the play area too (the lane is aspect-locked, so
   * height loss costs width — see .battlefield-frame in battle.css). Full stats
   * stay one tap away via the hand's ⓘ button.
   */
  compact?: boolean
}

export function CardTile({ card, canAfford = true, disabled = false, onClick, lockedSecs = 0, upgradeable = false , showDetails = false, displayCost, deckMatches = 0, selected = false, isNew = false, compact = false }: Props) {
  const heroLocked = card.isHero && lockedSecs > 0
  const clickable = canAfford && !disabled && !heroLocked
  const { openDetail, cardDetailNode } = useCardDetail()
  const synergyGroups = getSynergyGroups(card)

  let stats: string

  if (card.cardType === 'upgrade' && card.upgradeEffect) {
    const e = card.upgradeEffect
    stats = e.type === 'buffAttack'  ? `+${e.amount} ⚔`
          : e.type === 'healUnits'   ? `♥ +${e.amount}`
          : e.type === 'buffSpeed'   ? `+${e.amount} SPD`
          : e.type === 'buffMaxHp'   ? `+${e.amount} ♥`
          : e.type === 'buffRange'   ? `+${e.amount} RNG`
          : `UPGRADE`
  } else if (card.unit) {
    const u = card.unit
    if (u.isWall || u.attack === 0) {
      stats = `♥ ${u.maxHp}`
    } else {
      stats = `⚔ ${u.attack} / ♥ ${u.maxHp}`
    }
  } else {
    stats = ''
  }

  const isSecret = card.rarity === 'mythic' || card.rarity === 'shiny' || card.rarity === 'holofoil' || card.rarity === 'glass'
  const secretLabel: Record<string, string> = { mythic: 'MYTHIC', shiny: 'SHINY', holofoil: 'HOLO', glass: 'GLASS' }

  // The tile is the most-repeated interactive surface in the game — every
  // card grid in Collection, the deck builder, hand and pickers renders one
  // per card. Until now it was a plain <div onClick>: no role, no tab stop,
  // no keyboard activation, and its only name-ish attribute was `title`,
  // which isn't a reliable accessible name. A keyboard or screen-reader user
  // could not play, add, or pick a single card anywhere in the game (#2182).
  const ariaLabel = [
    card.name,
    CATEGORY_LABEL[getCardCategory(card)],
    `cost ${displayCost ?? card.cost}`,
  ].join(', ')

  return (
    <div
      className={[
        'card-tile',
        `card-tile--${card.rarity}`,
        clickable ? '' : 'card-tile--disabled',
        selected ? 'card-tile--selected' : '',
        compact ? 'card-tile--compact' : '',
      ].filter(Boolean).join(' ')}
      title={heroLocked ? `Hero cards unlock after 30 seconds (${lockedSecs}s remaining)` : card.description}
    >
      {/* The actual interactive surface. Used to be the outer div itself
          (role="button" + a manual keydown handler), but that nested a real
          <button> — the showDetails info button below — inside a role="button"
          ancestor, which axe (and several screen readers) flag as broken:
          nested interactive controls don't reliably expose both to assistive
          tech. A real <button> gets keyboard activation for free and can't
          contain another button, so the info button moves out to be its
          sibling instead, positioned back into the same corner with CSS. */}
      <button
        type="button"
        className="card-tile-btn"
        onClick={clickable ? onClick : undefined}
        disabled={!clickable}
        aria-label={ariaLabel}
        aria-pressed={selected ? true : undefined}
      >
        {isSecret && (
          <div className={`card-secret-badge card-secret-badge--${card.rarity}`}>
            {secretLabel[card.rarity]}
          </div>
        )}
        {isNew && !isSecret && (
          <div className="card-new-badge">NEW</div>
        )}
        {card.rarity === 'glass' && card.glassBreakChance && (
          <div className="card-glass-warning" title={`${Math.round(card.glassBreakChance * 100)}% chance to shatter on play`}><Icon name="crystal" size={12} /></div>
        )}
        {card.isHero && (
          <div className="hero-badge-wrap">
           <span className="hero-badge">HERO</span>
          </div>
        )}

        <div className={`card-cost${!canAfford ? ' card-cost--unaffordable' : ''}`}>{displayCost ?? card.cost}</div>
        {upgradeable && <div className="card-upgrade-badge">UPGRADE</div>}
        <div className={`card-title${isSecret || isNew ? ' card-title--badge' : ''}`}>{card.name}</div>
        <div className="card-art u-flex u-items-c u-just-c">
          <SynergyBadges groups={synergyGroups} deckMatches={deckMatches} />
          {card.unit
            ? <SpriteImg name={card.unit.name} className="card-sprite" />
            : card.upgradeEffect
              ? <SpriteImg name={card.name} fallbackName={UPGRADE_SPRITE[card.upgradeEffect.type] ?? 'upgrade'} className="card-sprite" />
              : card.augmentSlot
                ? <SpriteImg name={card.name} fallbackName={AUGMENT_SPRITE[card.augmentSlot] ?? 'augment-amulet'} className="card-sprite" />
                : null
          }
        </div>

        {!compact && <div className="card-stats">{stats}</div>}
        <div className={`card-bottom-row${showDetails ? ' card-bottom-row--with-info' : ''}`}>
          {!compact && <div className="card-rarity">{rarityStars(card.rarity)}</div>}
          <div className={`card-type-badge card-type-badge--${getCardCategory(card)}`}>
            {CATEGORY_ICON[getCardCategory(card)]}
            {/* The label carries its own ellipsis: text-overflow can't act on the
                badge itself, since that's a flex container and this span is a
                flex item rather than inline content it clips. */}
            <span className="card-type-badge-label">{CATEGORY_LABEL[getCardCategory(card)]}</span>
          </div>
        </div>
      </button>
      {showDetails && (
        <button
          className="card-bottom-info-btn"
          onClick={e => { e.stopPropagation(); openDetail(card) }}
          title="Card details"
          aria-label={`${card.name} card details`}
        >ⓘ</button>
      )}
      {heroLocked && (
        <div className="card-hero-lock u-absolute u-col u-items-c u-just-c">
          <span className="card-hero-lock-icon"><Icon name="timer" size={20} /></span>
          <span className="card-hero-lock-secs">{lockedSecs}s</span>
        </div>
      )}
      {cardDetailNode}
    </div>
  )
}
